import { faThumbtack } from "@fortawesome/pro-regular-svg-icons";
import { groupBy, last } from "lodash-es";

import { CopyTextIcon } from "src/base-components/CopyTextIcon";
import { Pill } from "src/base-components/Pill";
import { useCaseBlockScrollTo } from "src/cases/caseView/CaseBlockScrollHooks";
import { HistorySingleEntry } from "src/cases/caseView/history/HistoryItemFrame";
import { useCase } from "src/cases/queries/cases";
import { HistoryEntryPinnedEntitiesGroup as PinnedEntitiesUpdatedGroupType } from "src/cases/queries/casesTypes";
import { PinnedEntity } from "src/clients/case-management";
import { Tooltip } from "src/design-system/Tooltip";
import { findSchema, getSchemaIcon } from "src/entities/entityView/utils";
import { useEntitySchemas } from "src/entities/queries";
import { useWorkspaceContext } from "src/router/routerContextHooks";
import { CasePageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

export const PinnedEntitiesUpdatedGroup: React.FC<{
  entry: PinnedEntitiesUpdatedGroupType;
  isLast: boolean;
}> = ({ entry, isLast }) => {
  "use memo";

  const { events } = entry;
  const { case_id: caseId } = useParamsDecode<CasePageParams>();
  const { data: caseData } = useCase(caseId);
  const { workspace } = useWorkspaceContext();
  const { data: entitySchemas } = useEntitySchemas({
    baseUrl: workspace.base_url!,
  });
  const scrollToBlock = useCaseBlockScrollTo();

  // Use the first event (latest chronologically, since events are in reverse order)
  const firstEvent = last(events);

  if (!firstEvent) {
    // If such happened, then we have nothing to display
    return null;
  }

  // Consolidate all added and removed entities from all events in the group
  const allAddedEntities = events.flatMap(
    (event) => event.event_data.added_entities,
  );
  const allRemovedEntities = events.flatMap(
    (event) => event.event_data.removed_entities,
  );

  const eventData = firstEvent.event_data;
  const blockKey = eventData.block_key;

  // Resolve block_key to block name
  const blockConfig =
    caseData?.data.template_version?.case_blocks_configs?.[blockKey];
  const blockName = blockConfig?.title ?? blockKey;

  // Create tooltip content with Pinned/Unpinned sections
  const tooltipBody = (
    <div className="flex flex-col gap-y-3">
      {allAddedEntities.length > 0 && (
        <div className="flex flex-col gap-y-1.5">
          <div className="font-inter-semibold-13px">Pinned Entities</div>
          <EntityList
            entities={allAddedEntities}
            entitySchemas={entitySchemas}
          />
        </div>
      )}
      {allRemovedEntities.length > 0 && (
        <div className="flex flex-col gap-y-1.5">
          <div className="font-inter-semibold-13px">Unpinned Entities</div>
          <EntityList
            entities={allRemovedEntities}
            entitySchemas={entitySchemas}
          />
        </div>
      )}
    </div>
  );

  // Build headline with pill wrapped in Tooltip
  const headline = (
    <>
      <Tooltip body={tooltipBody} asChild>
        <span>
          <Pill dataLoc="pinned-entities-pill" size="sm" variant="gray">
            <Pill.Icon icon={faThumbtack} />
            <Pill.Text>Pinned Entities</Pill.Text>
          </Pill>
        </span>
      </Tooltip>{" "}
      in{" "}
      <Pill size="sm" variant="gray" onClick={() => scrollToBlock(blockKey)}>
        <Pill.Text>{blockName}</Pill.Text>
      </Pill>{" "}
      were modified
    </>
  );

  return (
    <HistorySingleEntry
      actorId={eventData.updated_by_user_id}
      eventType={firstEvent.event_type}
      headline={headline}
      icon={faThumbtack}
      isLast={isLast}
      timestamp={firstEvent.timestamp}
    />
  );
};

const MAX_DISPLAY_COUNT = 5;

const EntityList: React.FC<{
  entities: PinnedEntity[];
  entitySchemas: ReturnType<typeof useEntitySchemas>["data"];
}> = ({ entities, entitySchemas }) => {
  "use memo";

  // Group entities by entity_type
  const entitiesByType = groupBy(entities, "entity_type");

  return (
    <div className="flex flex-col gap-y-2">
      {Object.entries(entitiesByType).map(([entityType, entitiesOfType]) => {
        const schema = findSchema(entityType, entitySchemas);
        const displayName = schema?._display_name_plural || entityType;
        const icon = schema ? getSchemaIcon(schema) : undefined;

        const displayedEntities = entitiesOfType.slice(0, MAX_DISPLAY_COUNT);
        const remainingCount = entitiesOfType.length - MAX_DISPLAY_COUNT;

        return (
          <div key={entityType} className="flex flex-col gap-y-1">
            <div className="text-gray-300 font-inter-medium-13px">
              {displayName}
            </div>
            <div className="flex flex-wrap gap-1">
              {displayedEntities.map((entity) => (
                <Pill
                  key={entity.id}
                  dataLoc={`pinned-entity-${entity.id}`}
                  size="sm"
                  variant="dark-gray"
                >
                  {icon && <Pill.Icon icon={icon} />}
                  <Pill.Text fontType="code">{entity.id}</Pill.Text>
                  <CopyTextIcon
                    feedback="inline-only-checkmark"
                    size="3xs"
                    value={entity.id}
                  />
                </Pill>
              ))}
              {remainingCount > 0 && (
                <Pill size="sm" variant="dark-gray">
                  <Pill.Text>+{remainingCount}</Pill.Text>
                </Pill>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
